import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { t } from '@/lib/i18n';
import type { RorosCanvasItemDTO } from '@/types/roros/canvases';
import { Bot, Check, GripVertical, Pencil, Trash2, X } from 'lucide-react';

interface CanvasItemProps {
  item: RorosCanvasItemDTO;
  canEdit: boolean;
  isDragging: boolean;
  onEdit: (text: string) => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}

export function CanvasItem({
  item,
  canEdit,
  isDragging,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: CanvasItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);

  function saveEdit() {
    const next = draft.trim();
    if (!next) return;
    onEdit(next);
    setIsEditing(false);
  }

  function cancelEdit() {
    setDraft(item.text);
    setIsEditing(false);
  }

  return (
    <div
      className={`rounded-md border bg-yellow-50 p-2 text-sm text-yellow-950 shadow-sm transition ${isDragging ? 'opacity-50 ring-2 ring-primary-400' : ''}`}
      draggable={canEdit && !isEditing}
      onDragStart={(event) => {
        if (!canEdit) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', item.id);
        onDragStart();
      }}
      onDragOver={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDrop={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
      data-testid={`canvas-item-${item.id}`}
      data-source={item.source}
    >
      {isEditing ? (
        <div className="space-y-2">
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') saveEdit();
              if (event.key === 'Escape') cancelEdit();
            }}
            aria-label={t('Edit canvas item')}
            autoFocus
          />
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={cancelEdit} aria-label={t('Cancel edit')}>
              <X className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="primary" onClick={saveEdit} aria-label={t('Save edit')}>
              <Check className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-2">
          {canEdit && (
            <span className="mt-0.5 shrink-0 cursor-grab text-muted-foreground roros-canvas-no-print" aria-label={t('Drag to reorder')}>
              <GripVertical className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0 flex-1 basis-32 whitespace-normal break-words">{item.text}</div>
          {item.source === 'ai' && (
            <SimpleTooltip content={t('AI suggestion')}>
              <Badge variant="secondary" className="shrink-0 gap-1 border border-purple-200 bg-purple-50 text-purple-700" data-testid="canvas-ai-marker">
                <Bot className="h-3 w-3" />
                {t('AI')}
              </Badge>
            </SimpleTooltip>
          )}
          {canEdit && (
            <div className="flex shrink-0 gap-1 roros-canvas-no-print">
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} aria-label={t('Edit item')}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} aria-label={t('Delete item')}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
